import { useEffect, useRef, useState } from "react";

import bitcoin from "../../assets/chains/Bitcoin.png";
import solana from "../../assets/chains/solana.png";
import thorchain from "../../assets/chains/thorchain.png";
import mayaChain from "../../assets/chains/maya-chain.png";
import cosmos from "../../assets/chains/Cosmos.png";
import eth from "../../assets/chains/Ethereum.png";
import polygon from "../../assets/chains/polygon.png";
import arbitrum from "../../assets/chains/Arbitrum.png";
import base from "../../assets/chains/base.png";
import avalanche from "../../assets/chains/Avalanche.png";
import chainflipLogo from "../../assets/chainflip.svg";

/**
 * Animated cross-chain swap walkthrough that cycles through a handful
 * of representative routes:  FROM  →  hop(s)  →  TO
 *
 * The UI fires the hops one by one, lights up each node, then surfaces a
 * "Completed" summary before moving to the next route. The whole thing is
 * pure CSS + timers, no 3D library required.
 */

const ROUTES = [
  {
    from:  { symbol: "BTC",  name: "Bitcoin",   logo: bitcoin,    chain: "Bitcoin" },
    to:    { symbol: "ETH",  name: "Ethereum",  logo: eth,        chain: "Base" },
    hops:  [
      { label: "THORChain", logo: thorchain, kind: "Protocol" },
      { label: "Base L2",   logo: base,      kind: "Settlement" },
    ],
    meta: { fee: "0.4%", time: "~3m", slippage: "0.12%" },
  },
  {
    from:  { symbol: "SOL",  name: "Solana",    logo: solana,     chain: "Solana" },
    to:    { symbol: "USDC", name: "USDC",      logo: arbitrum,   chain: "Arbitrum" },
    hops:  [
      { label: "Chainflip", logo: chainflipLogo, kind: "Bridge", square: true },
      { label: "Arbitrum",  logo: arbitrum,      kind: "Settlement" },
    ],
    meta: { fee: "0.4%", time: "~90s", slippage: "0.08%" },
  },
  {
    from:  { symbol: "ETH",  name: "Ethereum",  logo: eth,        chain: "Ethereum" },
    to:    { symbol: "ATOM", name: "Cosmos",    logo: cosmos,     chain: "Cosmos Hub" },
    hops:  [
      { label: "Maya",      logo: mayaChain, kind: "Protocol" },
      { label: "IBC",       logo: cosmos,    kind: "Transport" },
    ],
    meta: { fee: "0.4%", time: "~2m", slippage: "0.15%" },
  },
  {
    from:  { symbol: "AVAX", name: "Avalanche", logo: avalanche,  chain: "Avalanche" },
    to:    { symbol: "MATIC",name: "Polygon",   logo: polygon,    chain: "Polygon" },
    hops:  [
      { label: "THORChain", logo: thorchain, kind: "Protocol" },
    ],
    meta: { fee: "0.4%", time: "~70s", slippage: "0.09%" },
  },
];

const STEP_MS = 900;
const COMPLETE_HOLD_MS = 2600;

export function SwapWalkthrough() {
  const [routeIdx, setRouteIdx] = useState(0);
  const [activeStep, setActiveStep] = useState(0); // 0 = from, 1..n = hops, n+1 = destination, n+2 = completed
  const timersRef = useRef([]);
  const reducedRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = mq.matches;
    const onMq = () => { reducedRef.current = mq.matches; };
    mq.addEventListener?.("change", onMq);
    return () => mq.removeEventListener?.("change", onMq);
  }, []);

  useEffect(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];

    if (reducedRef.current) {
      setActiveStep(ROUTES[routeIdx].hops.length + 2);
      const t = window.setTimeout(() => {
        setRouteIdx((i) => (i + 1) % ROUTES.length);
      }, 4200);
      timersRef.current.push(t);
      return () => timersRef.current.forEach((id) => window.clearTimeout(id));
    }

    setActiveStep(0);
    const hops = ROUTES[routeIdx].hops.length;
    const totalSteps = hops + 2; // from + hops + destination
    // Step progression: activate from, hops sequentially, destination, then "completed"
    for (let s = 1; s <= totalSteps; s += 1) {
      const id = window.setTimeout(() => setActiveStep(s), s * STEP_MS);
      timersRef.current.push(id);
    }
    // Mark completed
    const completedId = window.setTimeout(
      () => setActiveStep(totalSteps + 1),
      (totalSteps + 1) * STEP_MS
    );
    timersRef.current.push(completedId);
    // Advance to next route
    const nextId = window.setTimeout(
      () => setRouteIdx((i) => (i + 1) % ROUTES.length),
      (totalSteps + 1) * STEP_MS + COMPLETE_HOLD_MS
    );
    timersRef.current.push(nextId);

    return () => timersRef.current.forEach((id) => window.clearTimeout(id));
  }, [routeIdx]);

  const route = ROUTES[routeIdx];
  const totalNodes = route.hops.length + 2; // from + hops + destination
  const isCompleted = activeStep > totalNodes;
  const progressPercent = Math.min(activeStep / totalNodes, 1) * 100;

  return (
    <div
      className="swapwalk"
      role="img"
      aria-label={`Cross-chain swap: ${route.from.symbol} on ${route.from.chain} to ${route.to.symbol} on ${route.to.chain}`}
      data-testid="swap-walkthrough"
    >
      <div className="swapwalk__card">
        <header className="swapwalk__head">
          <span className="swapwalk__pill swapwalk__pill--live">
            <span className="swapwalk__pill-dot" />
            Live route
          </span>
          <span className="swapwalk__head-meta font-dm">
            {isCompleted ? "Completed" : "Finding best route…"}
          </span>
        </header>

        {/* From/To token summary */}
        <div className="swapwalk__summary">
          <div className="swapwalk__token swapwalk__token--from">
            <div className="swapwalk__token-logo">
              <img src={route.from.logo} alt="" aria-hidden="true" />
            </div>
            <div className="swapwalk__token-meta">
              <span className="swapwalk__token-symbol font-syne">{route.from.symbol}</span>
              <span className="swapwalk__token-chain font-dm">{route.from.chain}</span>
            </div>
          </div>

          <div className="swapwalk__arrow" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m-5-5l5 5-5 5" />
            </svg>
          </div>

          <div className="swapwalk__token swapwalk__token--to">
            <div className="swapwalk__token-logo">
              <img src={route.to.logo} alt="" aria-hidden="true" />
            </div>
            <div className="swapwalk__token-meta">
              <span className="swapwalk__token-symbol font-syne">{route.to.symbol}</span>
              <span className="swapwalk__token-chain font-dm">{route.to.chain}</span>
            </div>
          </div>
        </div>

        {/* Animated route path */}
        <div className="swapwalk__route">
          <div className="swapwalk__line">
            <div
              className="swapwalk__line-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="swapwalk__nodes">
            {/* FROM node */}
            <RouteNode
              idx={0}
              activeStep={activeStep}
              label={route.from.chain}
              kind="Source"
              logo={route.from.logo}
            />

            {/* HOPS */}
            {route.hops.map((hop, i) => (
              <RouteNode
                key={`${routeIdx}-hop-${i}`}
                idx={i + 1}
                activeStep={activeStep}
                label={hop.label}
                kind={hop.kind}
                logo={hop.logo}
                square={hop.square}
              />
            ))}

            {/* TO node */}
            <RouteNode
              idx={route.hops.length + 1}
              activeStep={activeStep}
              label={route.to.chain}
              kind="Destination"
              logo={route.to.logo}
            />
          </div>
        </div>

        {/* Bottom bar: fee / time / slippage OR completed */}
        <div className={`swapwalk__footer ${isCompleted ? "swapwalk__footer--complete" : ""}`}>
          {isCompleted ? (
            <div className="swapwalk__complete" data-testid="swap-walkthrough-complete">
              <span className="swapwalk__complete-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              </span>
              <span className="font-dm">
                Settled {route.to.symbol} on {route.to.chain} · {route.meta.time}
              </span>
            </div>
          ) : (
            <>
              <div className="swapwalk__metric">
                <span className="swapwalk__metric-label font-dm">Aggregator fee</span>
                <span className="swapwalk__metric-value font-syne">{route.meta.fee}</span>
              </div>
              <div className="swapwalk__metric">
                <span className="swapwalk__metric-label font-dm">ETA</span>
                <span className="swapwalk__metric-value font-syne">{route.meta.time}</span>
              </div>
              <div className="swapwalk__metric">
                <span className="swapwalk__metric-label font-dm">Slippage</span>
                <span className="swapwalk__metric-value font-syne">{route.meta.slippage}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Pagination dots */}
      <div className="swapwalk__dots" role="tablist" aria-label="Swap route examples">
        {ROUTES.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === routeIdx}
            aria-label={`Show route ${i + 1}`}
            className={`swapwalk__dot ${i === routeIdx ? "swapwalk__dot--active" : ""}`}
            onClick={() => setRouteIdx(i)}
          />
        ))}
      </div>
    </div>
  );
}

function RouteNode({ idx, activeStep, label, kind, logo, square }) {
  const state =
    activeStep > idx ? "done"
    : activeStep === idx ? "active"
    : "pending";

  return (
    <div className={`swapwalk-node swapwalk-node--${state}`} data-idx={idx}>
      <div className={`swapwalk-node__disc ${square ? "swapwalk-node__disc--square" : ""}`}>
        <img src={logo} alt="" aria-hidden="true" />
        {state === "active" ? (
          <span className="swapwalk-node__ring" aria-hidden="true" />
        ) : null}
      </div>
      <div className="swapwalk-node__meta">
        <span className="swapwalk-node__kind font-dm">{kind}</span>
        <span className="swapwalk-node__label font-dm">{label}</span>
      </div>
    </div>
  );
}

export default SwapWalkthrough;
